import { AddRounded as AddRoundedIcon, DeleteOutline as DeleteOutlineIcon } from "@mui/icons-material";
import { Button, IconButton, TextField } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { getLocale } from "../../../../i18n";
import useNoti from "../../../../hooks/useNoti";
import { useTranslation } from "../../../../i18n/useTranslation";
import { AppNotifications } from "../../../../models";
import { FeedbackService } from "../../../../services/feedback.service";
import { MESSAGES } from "../../../constants/mock";
import Loading from "../../../components/Loading";
import "./index.scss";

const emptyRow = () => "";

type FeedbackLogItem = {
  id: string;
  topic: string;
  topicLabel: string;
  message: string;
  mailSent: boolean;
  createdAt: string;
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(getLocale() === "en" ? "en-US" : "vi-VN", {
    timeZone: "Asia/Ho_Chi_Minh",
    hour12: false,
  });
};

const FeedbackMail: React.FC = () => {
  const { t } = useTranslation();
  const [pushNoti] = useNoti();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toEmails, setToEmails] = useState<string[]>([emptyRow()]);
  const [ccEmails, setCcEmails] = useState<string[]>([emptyRow()]);
  const [logs, setLogs] = useState<FeedbackLogItem[]>([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [logsTotal, setLogsTotal] = useState(0);

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      const res = await FeedbackService.getMailConfig();
      const data = res.data?.data || {};
      setToEmails(data.to?.length ? data.to : [emptyRow()]);
      setCcEmails(data.cc?.length ? data.cc : [emptyRow()]);
    } catch (error) {
      console.error(error);
      pushNoti(AppNotifications.ERROR, MESSAGES.ERROR);
    } finally {
      setLoading(false);
    }
  }, [pushNoti]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const fetchLogs = useCallback(async () => {
    try {
      setLogsLoading(true);
      const res = await FeedbackService.listLogs({ page: 1, limit: 50 });
      setLogs(res.data?.data?.items || []);
      setLogsTotal(res.data?.data?.pagination?.total || 0);
    } catch (error) {
      console.error(error);
      setLogs([]);
    } finally {
      setLogsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const updateRow = (
    list: string[],
    setter: (next: string[]) => void,
    index: number,
    value: string,
  ) => {
    const next = [...list];
    next[index] = value;
    setter(next);
  };

  const addRow = (list: string[], setter: (next: string[]) => void) => {
    setter([...list, emptyRow()]);
  };

  const removeRow = (list: string[], setter: (next: string[]) => void, index: number) => {
    const next = list.filter((_, idx) => idx !== index);
    setter(next.length ? next : [emptyRow()]);
  };

  const save = async () => {
    try {
      setSaving(true);
      const to = toEmails.map((email) => email.trim()).filter(Boolean);
      const cc = ccEmails.map((email) => email.trim()).filter(Boolean);
      const res = await FeedbackService.updateMailConfig({ to, cc });
      if (res.status === 200 || res.status === 201) {
        const data = res.data?.data || {};
        setToEmails(data.to?.length ? data.to : [emptyRow()]);
        setCcEmails(data.cc?.length ? data.cc : [emptyRow()]);
        pushNoti(AppNotifications.SUCCESS, MESSAGES.SUCCESS);
      } else {
        pushNoti(AppNotifications.ERROR, res.data?.message || MESSAGES.ERROR);
      }
    } catch (error) {
      pushNoti(AppNotifications.ERROR, MESSAGES.ERROR);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Loading />;
  }

  const renderEmailList = (
    title: string,
    hint: string,
    list: string[],
    setter: (next: string[]) => void,
  ) => (
    <div className="feedback-mail__group">
      <div className="feedback-mail__group-head">
        <div>
          <h4>{title}</h4>
          <p>{hint}</p>
        </div>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddRoundedIcon />}
          onClick={() => addRow(list, setter)}
        >
          {t("feedback.addEmail")}
        </Button>
      </div>
      {list.map((email, index) => (
        <div className="feedback-mail__row" key={`${title}-${index}`}>
          <TextField
            fullWidth
            type="email"
            size="small"
            placeholder="name@company.com"
            value={email}
            onChange={(event) => updateRow(list, setter, index, event.target.value)}
          />
          <IconButton
            aria-label={t("common.delete")}
            onClick={() => removeRow(list, setter, index)}
          >
            <DeleteOutlineIcon />
          </IconButton>
        </div>
      ))}
    </div>
  );

  return (
    <div className="feedback-mail">
      <div className="feedback-mail__header">
        <div>
          <h3>{t("feedback.title")}</h3>
          <p>{t("feedback.hint")}</p>
        </div>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? t("feedback.saving") : t("common.save")}
        </Button>
      </div>
      {renderEmailList(t("feedback.to"), t("feedback.toHint"), toEmails, setToEmails)}
      {renderEmailList(t("feedback.cc"), t("feedback.ccHint"), ccEmails, setCcEmails)}

      <div className="feedback-mail__logs">
        <div className="feedback-mail__logs-head">
          <div>
            <h4>{t("feedback.logsTitle")}</h4>
            <p>{t("feedback.logsHint", { count: logsTotal })}</p>
          </div>
          <Button variant="outlined" size="small" onClick={fetchLogs} disabled={logsLoading}>
            {t("common.refresh")}
          </Button>
        </div>
        <div className="feedback-mail__table">
          <div className="feedback-mail__table-row feedback-mail__table-row--head">
            <div>{t("feedback.logTime")}</div>
            <div>{t("feedback.logTopic")}</div>
            <div>{t("feedback.logMessage")}</div>
            <div>{t("feedback.logMail")}</div>
          </div>
          {logsLoading ? (
            <div className="feedback-mail__empty">{t("feedback.logsLoading")}</div>
          ) : logs.length === 0 ? (
            <div className="feedback-mail__empty">{t("feedback.logsEmpty")}</div>
          ) : (
            logs.map((item) => (
              <div className="feedback-mail__table-row" key={item.id}>
                <div>{formatDateTime(item.createdAt)}</div>
                <div>{item.topicLabel || item.topic}</div>
                <div className="feedback-mail__message">{item.message}</div>
                <div>
                  {item.mailSent ? t("feedback.mailSent") : t("feedback.mailNotSent")}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default FeedbackMail;
